import { cvEditionFields } from './cv-edition-edit';

/** Closed visible facts only: source hashes, database identities and private notes are never a diff. */
export function compareCvHistory(left: Record<string, unknown>, right: Record<string, unknown>, options: { knownFieldsOnly?: boolean } = {}) {
  const before = new Map(cvEditionFields(left).map(field => [JSON.stringify(field.path), field]));
  const after = new Map(cvEditionFields(right).map(field => [JSON.stringify(field.path), field]));
  return [...new Set([...before.keys(), ...after.keys()])].flatMap(key => {
    const a = before.get(key), b = after.get(key);
    if (options.knownFieldsOnly && (!a || !b)) return [];
    return a?.value === b?.value ? [] : [{ field: b ?? a!, before: a?.value ?? '', after: b?.value ?? '' }];
  });
}
export function cvHistoryAction(operation: string): string {
  if (operation === 'baseline' || operation === 'legacy.capture') return 'observed';
  if (/restore/.test(operation)) return 'restored';
  if (operation === 'release.approve') return 'approved';
  if (operation === 'translation.accept') return 'translationAccepted';
  if (/create|duplicate|fork/.test(operation)) return 'created';
  if (/import|replace/.test(operation)) return 'imported';
  if (/health/.test(operation)) return 'health';
  if (/tailor|enhance|generate/.test(operation)) return 'generated';
  return 'edited';
}

/** Older snapshots contain only stored overrides. Never fill their gaps from
 * the current profile: display those authored fields and label the limitation. */
export function cvDraftHistoryView(snapshot: unknown): { document: Record<string, unknown> | null; partialSnapshot: boolean } {
  const object = (value: unknown): Record<string, unknown> | null => value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null;
  const stored = object(snapshot);
  if (!stored) return { document: null, partialSnapshot: true };
  const resolved = object(stored.resolved);
  if (resolved) return { document: resolved, partialSnapshot: false };
  const sections = object(stored.sections);
  const header = object(sections?.header);
  const document: Record<string, unknown> = {};
  for (const key of ['name', 'title', 'bio', 'location', 'contactEmail']) {
    if (header?.[key] === null || typeof header?.[key] === 'string') document[key] = header?.[key];
  }
  if (stored.summary === null || typeof stored.summary === 'string') document.summary = stored.summary;
  for (const key of ['experiences', 'educations', 'certifications', 'highlights', 'projects', 'skillGroups', 'languages']) {
    if (Array.isArray(sections?.[key])) document[key] = sections?.[key];
  }
  return { document: cvEditionFields(document).length ? document : null, partialSnapshot: true };
}
